import { useCallback, useMemo } from 'react'
import { type Product } from '@/features/shop/services/Product/types'
import { ShelfViewAllButton } from '@/features/shop/components/Shelf/ShelfViewAllButton'
import { ProductCard } from '@/features/shop/components/ProductCard'
import {
  type ProductShelfTrackingProps,
  useOnShelfProduct,
} from '@/features/shop/components/Shelf/interactions/useOnShelfProduct'
import { useUpdateProductQuantity } from '@/features/shared/hooks/useUpdateProductQuantity'
import {
  type OnProductArgs,
  type RenderQuickAdd,
} from '@/features/shop/components/ProductCard/types'
import styled from 'styled-components'
import { HERO_SHELF_ITEM_WIDTH, SHELF_ITEM_WIDTH } from '@/features/shared/constants/shelves'
import { screenSizes } from '@shipt/design-system-themes'
import { LandscapeProductCard } from '@/features/shop/components/LandscapeProductCard'
import { useBasket } from '@/features/shared/hooks/useBasket'
import { routes } from '@shared/constants/routes'
import { type ShelfViewAllProps } from '@/features/shop/components/Shelf'

export type UseRenderShelfProductsProps = {
  shelfName: string
  shelfIndex: number
  viewAll?: ShelfViewAllProps
  isSponsoredShelf?: boolean
  handleOnProductClick?: (args: OnProductArgs, e: React.MouseEvent) => Promise<unknown>
  landscape?: boolean
  products: Product[]
  wrapper?: typeof DefaultWrapper
  trackingProps?: ProductShelfTrackingProps
  renderQuickAdd?: RenderQuickAdd
  showLargerProducts?: boolean
  showAddToListButton?: boolean
  hideRatings?: boolean
  analyticSlug?: string
  hasBackground?: boolean
  hideSecondaryDetails?: boolean
  shelfCategory?: string
  shelfCategoryIndex?: number
  linkToUniversalPDP: boolean
  sharedListId?: string
}

export const useRenderShelfProducts = ({
  shelfName,
  shelfIndex,
  viewAll,
  isSponsoredShelf,
  handleOnProductClick,
  landscape = false,
  products,
  wrapper,
  trackingProps = {},
  renderQuickAdd,
  showLargerProducts = false,
  showAddToListButton = false,
  hideRatings,
  analyticSlug,
  hasBackground = false,
  hideSecondaryDetails = false,
  shelfCategory,
  shelfCategoryIndex,
  linkToUniversalPDP,
  sharedListId,
}: UseRenderShelfProductsProps) => {
  const { onQtyAdded, onQtyRemoved, onProductClick, onProductView } = useOnShelfProduct({
    shelfName,
    shelfIndex,
    shelfCategory,
    shelfCategoryIndex,
    analyticSlug,
    ...trackingProps,
  })

  const handleOnClick = useCallback(
    async (args: OnProductArgs, e: React.MouseEvent) => {
      await handleOnProductClick?.(args, e)
      onProductClick(args)
    },
    [onProductClick, handleOnProductClick],
  )

  const { getProductQuantity, getProductPromotionMetadata } = useBasket()
  const { updateProductQuantity } = useUpdateProductQuantity()

  const sharedListHref = sharedListId ? `${routes.LIST.url}/${sharedListId}` : undefined

  return useMemo(() => {
    return products.map((product, productIndex) => {
      if (landscape && product.oos && !sharedListId) return null
      const itemsLength = products.length
      const Component = landscape ? LandscapeProductCard : ProductCard
      const WrapperComponent = wrapper ?? DefaultWrapper

      return (
        <WrapperComponent
          key={product.id}
          data-landscape={landscape}
          data-has-background={hasBackground}
          data-show-larger-products={showLargerProducts}
        >
          {viewAll?.viewAllHref && itemsLength > 9 && productIndex === itemsLength - 1 ? (
            <ShelfViewAllButton
              onClick={viewAll.onViewAll}
              href={viewAll.viewAllHref}
              aria-label={`View All ${shelfName} Products`}
            />
          ) : (
            <Component
              product={product}
              onQtyAdded={onQtyAdded}
              onQtyRemoved={onQtyRemoved}
              onClick={handleOnClick}
              onInView={onProductView}
              index={productIndex}
              isInSponsoredShelf={isSponsoredShelf}
              updateProductQuantity={updateProductQuantity}
              renderQuickAdd={renderQuickAdd}
              showLargerProducts={showLargerProducts}
              showAddToListButton={showAddToListButton}
              hideRatings={hideRatings}
              hasBackground={hasBackground}
              hideSecondaryDetails={hideSecondaryDetails}
              productQuantity={getProductQuantity(product)}
              promotionMetadata={getProductPromotionMetadata(product)}
              linkToUniversalPDP={linkToUniversalPDP}
              {...(landscape && { href: sharedListHref })}
            />
          )}
        </WrapperComponent>
      )
    })
  }, [
    viewAll,
    landscape,
    handleOnClick,
    onProductView,
    onQtyAdded,
    onQtyRemoved,
    products,
    getProductPromotionMetadata,
    shelfName,
    isSponsoredShelf,
    wrapper,
    showLargerProducts,
    getProductQuantity,
    updateProductQuantity,
    renderQuickAdd,
    hideRatings,
    hasBackground,
    hideSecondaryDetails,
    linkToUniversalPDP,
    showAddToListButton,
    sharedListHref,
    sharedListId,
  ])
}

const DefaultWrapper = styled.div<{
  'data-landscape': boolean
  'data-has-background': boolean
  'data-show-larger-products': boolean
}>`
  margin: 0.625rem 0 0 0.625rem;
  position: relative;
  width: 146px;

  &[data-landscape='true'] {
    width: ${HERO_SHELF_ITEM_WIDTH}px;
  }

  &[data-has-background='true'] {
    margin: 0;
    background-color: #ffffff;
    padding: 8px;
    border-radius: 12px;
    width: ${SHELF_ITEM_WIDTH + 16}px;
  }

  &[data-has-background='true'][data-landscape='true'] {
    display: flex;
    flex-direction: column;
    justify-content: center;
    width: ${HERO_SHELF_ITEM_WIDTH}px;
  }

  &[data-show-larger-products='true'] {
    max-width: 12rem;
    width: 100%;
  }

  @media ${screenSizes.tablet} {
    width: ${SHELF_ITEM_WIDTH}px;

    &[data-has-background='true'] {
      height: 100%;
    }
  }
`
